/**
 * Presentation helpers shared by the customer pages.
 *
 * Booking times are treated as UTC wall-clock throughout the dashboard — see
 * `formatUtc` in Calendar.jsx. Keep that here: a customer comparing their
 * appointment against the business's calendar must read the same clock time,
 * and silently shifting it into the browser's timezone is how someone shows up
 * an hour late.
 */

const UTC = { timeZone: "UTC" };

export const formatDateTime = (value, locale) =>
  value
    ? new Date(value).toLocaleString(locale, {
        ...UTC,
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "-";

export const formatDate = (value, locale) =>
  value
    ? new Date(value).toLocaleDateString(locale, { ...UTC, dateStyle: "full" })
    : "-";

export const formatTime = (value, locale) =>
  value
    ? new Date(value).toLocaleTimeString(locale, { ...UTC, timeStyle: "short" })
    : "-";

/**
 * The "YYYY-MM-DD" the API expects, read off the UTC clock rather than the
 * browser's. A booking at 00:30 UTC would otherwise submit as the previous day
 * for anyone west of Greenwich.
 */
export const toApiDate = (value) => new Date(value).toISOString().slice(0, 10);

export const statusBadgeClass = (status) => {
  switch (status?.toLowerCase()) {
    case "confirmed":
      return "bg-brand-50 text-brand-700";
    case "pending":
      return "bg-amber-100 text-amber-800";
    case "cancelled":
      return "bg-red-50 text-red-700";
    case "completed":
      return "bg-sky-100 text-sky-800";
    default:
      return "bg-ink-100 text-ink-600";
  }
};

/**
 * Whole days from today until a booking, in UTC-day terms so "tomorrow" flips
 * at midnight rather than 24 hours out.
 */
export const daysUntil = (value) => {
  const dayOf = (d) => Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  return Math.round(
    (dayOf(new Date(value)) - dayOf(new Date())) / 86400000,
  );
};
